import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import {
  ArrowLeft,
  Plus,
  Trash2,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronRight,
  Paperclip,
} from 'lucide-react';
import type { User } from '../App';
import { categories, stages } from '../lib/mockData';
import {
  createSubmission,
  uploadDocuments,
  finalizeSubmission,
  type CreateSubmissionData,
} from '../api/innovations';
import { getInstitutionalDashboard, type InstitutionalDashboardData } from '../api/institutions';

interface BulkUploadPageProps {
  user: User;
  onBack: () => void;
}

const MAX_DESCRIPTION = 3000;
const MAX_IP_UNIQUENESS = 2000;
const MIN_IP_UNIQUENESS = 20;
const MAX_DOCS_PER_ROW = 10;

/** Row shape for the entry table */
export interface BulkUploadRow {
  id: number;
  submitterEmail: string;
  title: string;
  category: string;
  stage: string;
  description: string;
  ipUniqueness: string;
  targetUsers: string;
  keywords: string;
  documents: File[];
}

/** Result shape after submitting a row */
export interface BulkUploadResult extends BulkUploadRow {
  success: boolean;
  submissionId?: string;
  error?: string;
}

/** Map frontend stage (mockData) to backend current_state (validation.js enum) */
function mapStageToCurrentState(
  stage: string
): 'idea' | 'exploration' | 'concept' | 'prototype' | 'demo' {
  const stageMap: Record<
    string,
    'idea' | 'exploration' | 'concept' | 'prototype' | 'demo'
  > = {
    prototype: 'prototype',
    pilot: 'demo',
    'market-ready': 'demo',
    idea: 'idea',
    exploration: 'exploration',
    concept: 'concept',
    demo: 'demo',
  };
  return stageMap[stage.toLowerCase()] || 'idea';
}

/** Validation rules mirroring validation.js */
function validateRow(row: BulkUploadRow): Partial<Record<keyof BulkUploadRow, string>> {
  const errors: Partial<Record<keyof BulkUploadRow, string>> = {};
  const title = (row.title || '').trim();
  const description = (row.description || '').trim();
  const ipU = (row.ipUniqueness || '').trim();
  const email = (row.submitterEmail || '').trim();

  if (!title) errors.title = 'Title is required';
  else if (title.length < 10) errors.title = `At least 10 characters (${title.length}/10)`;
  else if (title.length > 200) errors.title = 'Maximum 200 characters';

  if (!description) errors.description = 'Description is required';
  else if (description.length < 50)
    errors.description = `At least 50 characters (${description.length}/50)`;
  else if (description.length > MAX_DESCRIPTION)
    errors.description = `Maximum ${MAX_DESCRIPTION} characters`;

  if (!ipU) errors.ipUniqueness = 'IP uniqueness is required';
  else if (ipU.length < MIN_IP_UNIQUENESS)
    errors.ipUniqueness = `At least ${MIN_IP_UNIQUENESS} characters (${ipU.length}/${MIN_IP_UNIQUENESS})`;
  else if (ipU.length > MAX_IP_UNIQUENESS)
    errors.ipUniqueness = `Maximum ${MAX_IP_UNIQUENESS} characters`;

  if (!row.stage) errors.stage = 'Select a stage';
  else {
    const validStates = ['idea', 'exploration', 'concept', 'prototype', 'demo'];
    const mapped = mapStageToCurrentState(row.stage);
    if (!validStates.includes(mapped)) errors.stage = 'Invalid stage';
  }

  if (!email) errors.submitterEmail = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    errors.submitterEmail = 'Enter a valid email address';

  const category = (row.category || '').trim();
  if (category.length > 100) errors.category = 'Maximum 100 characters';

  const targetUsers = (row.targetUsers || '').trim();
  if (targetUsers.length > 500) errors.targetUsers = 'Maximum 500 characters';

  return errors;
}

let rowIdCounter = 1;
function createEmptyRow(): BulkUploadRow {
  return {
    id: rowIdCounter++,
    submitterEmail: '',
    title: '',
    category: '',
    stage: '',
    description: '',
    ipUniqueness: '',
    targetUsers: '',
    keywords: '',
    documents: [],
  };
}

const FILE_ACCEPT = '.pdf,.doc,.docx';

export function BulkUploadPage({ user, onBack }: BulkUploadPageProps) {
  const [phase, setPhase] = useState<'entry' | 'review' | 'submitting' | 'results'>('entry');
  const [rows, setRows] = useState<BulkUploadRow[]>(() => [createEmptyRow()]);
  const [touched, setTouched] = useState<Record<number, Partial<Record<keyof BulkUploadRow, boolean>>>>({});
  const [results, setResults] = useState<BulkUploadResult[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const [dashboardData, setDashboardData] = useState<InstitutionalDashboardData | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const remainingSlots = dashboardData?.institution?.slots_remaining ?? 0;
  const isSuspended = dashboardData?.institution?.status === 'suspended';
  const slotsLeft = Math.max(0, remainingSlots - rows.length);

  useEffect(() => {
    let cancelled = false;
    setDashboardLoading(true);
    setDashboardError(null);
    getInstitutionalDashboard()
      .then((d) => {
        if (!cancelled) setDashboardData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setDashboardError((e as { message?: string })?.message ?? 'Failed to load dashboard');
      })
      .finally(() => {
        if (!cancelled) setDashboardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const touch = (rowId: number, field: keyof BulkUploadRow) => {
    setTouched((t) => ({
      ...t,
      [rowId]: { ...(t[rowId] ?? {}), [field]: true },
    }));
  };

  const touchAll = () => {
    const next: Record<number, Partial<Record<keyof BulkUploadRow, boolean>>> = {};
    rows.forEach((row) => {
      next[row.id] = {
        submitterEmail: true,
        title: true,
        stage: true,
        description: true,
        ipUniqueness: true,
        category: true,
        targetUsers: true,
      };
    });
    setTouched(next);
  };

  const updateRow = <K extends keyof BulkUploadRow>(id: number, field: K, value: BulkUploadRow[K]) => {
    setRows((r) => r.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const appendDocuments = (rowId: number, fileList: FileList | null) => {
    if (!fileList?.length) return;
    setRows((r) =>
      r.map((row) => {
        if (row.id !== rowId) return row;
        const next = [...row.documents];
        for (let i = 0; i < fileList.length && next.length < MAX_DOCS_PER_ROW; i++) {
          next.push(fileList[i]);
        }
        return { ...row, documents: next };
      })
    );
  };

  const removeDocumentAt = (rowId: number, index: number) => {
    setRows((r) =>
      r.map((row) =>
        row.id === rowId
          ? { ...row, documents: row.documents.filter((_, i) => i !== index) }
          : row
      )
    );
  };

  const addRow = () => {
    if (rows.length >= remainingSlots) return;
    setRows((r) => [...r, createEmptyRow()]);
  };

  const removeRow = (id: number) => {
    if (rows.length === 1) return;
    setRows((r) => r.filter((row) => row.id !== id));
  };

  const rowErrors = rows.map((row) => ({ id: row.id, errors: validateRow(row) }));
  const hasAnyErrors = rowErrors.some((r) => Object.keys(r.errors).length > 0);
  const getErrors = (id: number) => rowErrors.find((r) => r.id === id)?.errors ?? {};
  const isErr = (id: number, field: keyof BulkUploadRow) =>
    !!(touched[id]?.[field] && getErrors(id)[field]);
  const getMsg = (id: number, field: keyof BulkUploadRow) =>
    touched[id]?.[field] ? getErrors(id)[field] : null;

  const rowsWithoutDoc = rows.filter((r) => r.documents.length === 0);
  const progressPct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;

  const handleReview = () => {
    touchAll();
    if (!hasAnyErrors) setPhase('review');
  };

  const submitSingleRow = async (row: BulkUploadRow): Promise<BulkUploadResult> => {
    const title = row.title.trim();
    const abstract = row.description.trim();
    const keywordsArray = (row.keywords || '')
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);
    const payload: CreateSubmissionData = {
      title,
      abstract,
      current_state: mapStageToCurrentState(row.stage),
      keywords: keywordsArray,
      technology_area: row.category?.trim() || undefined,
      expected_outcome: row.targetUsers?.trim() || undefined,
      ip_uniqueness: row.ipUniqueness.trim(),
    };

    try {
      const submission = await createSubmission(payload);

      if (row.documents.length > 0) {
        try {
          await uploadDocuments(submission.submission_uuid, row.documents);
        } catch (docErr: unknown) {
          const err = docErr as { message?: string; response?: { data?: { message?: string } } };
          const msg = err?.response?.data?.message ?? err?.message ?? 'Upload failed';
          return {
            ...row,
            success: true,
            submissionId: submission.submission_uuid,
            error: `Submitted; document failed: ${msg}`,
          };
        }
      }

      await finalizeSubmission(submission.submission_uuid);
      return { ...row, success: true, submissionId: submission.submission_uuid };
    } catch (err: unknown) {
      const e = err as { message?: string; response?: { data?: { message?: string; error?: string } } };
      let message = e?.message ?? 'Submission failed';
      if (e?.response?.data?.details && Array.isArray(e.response.data.details)) {
        message = (e.response.data.details as { msg?: string }[])
          .map((d) => d.msg)
          .filter(Boolean)
          .join(' ');
      } else if (e?.response?.data?.message) {
        message = e.response.data.message;
      } else if (e?.response?.data?.error) {
        message = e.response.data.error;
      }
      return { ...row, success: false, error: message };
    }
  };

  const handleSubmitAll = async () => {
    setPhase('submitting');
    setProgress({ done: 0, total: rows.length });
    const res: BulkUploadResult[] = [];
    for (const row of rows) {
      const result = await submitSingleRow(row);
      res.push(result);
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setResults(res);
    setPhase('results');
  };

  const handleReset = () => {
    rowIdCounter = 1;
    setRows([createEmptyRow()]);
    setPhase('entry');
    setResults([]);
    setTouched({});
    setProgress({ done: 0, total: 0 });
  };

  const inputErrorClass = (id: number, field: keyof BulkUploadRow) =>
    isErr(id, field)
      ? 'border-red-300 bg-red-50 focus-visible:ring-red-200 focus-visible:border-red-400'
      : '';

  if (dashboardLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
      </div>
    );
  }

  if (dashboardError || !dashboardData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-700">{dashboardError ?? 'Unable to load institution data.'}</p>
            <Button variant="outline" className="mt-4" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSuspended || remainingSlots === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            {isSuspended
              ? 'Account suspended. You cannot add bulk uploads.'
              : 'No slots remaining. Contact admin for renewal.'}
          </AlertDescription>
        </Alert>
        <Button variant="ghost" className="mt-4 text-[var(--navy)]" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to dashboard
        </Button>
      </div>
    );
  }

  // ——— Phase 1: Entry ———
  if (phase === 'entry') {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Button
          variant="ghost"
          className="mb-4 text-[var(--navy)] hover:bg-[var(--gold)]/10"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to dashboard
        </Button>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[var(--navy)]">
              Bulk Innovation Upload
            </h1>
            <p className="text-slate-600 mt-0.5">
              Add multiple innovations at once — each row uses one institutional slot.
            </p>
          </div>
          <Badge variant="secondary" className="text-[var(--navy)]">
            {rows.length} of {remainingSlots} slots · {slotsLeft} remaining
          </Badge>
        </div>

        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <AlertDescription className="text-blue-800 text-sm">
            <strong>How it works:</strong> Fill in each innovation&apos;s details below. Required
            fields are marked with <span className="text-red-500">*</span>. Click{' '}
            <strong>Add Row</strong> to include more innovations (up to your {remainingSlots}{' '}
            available slots). When ready, click <strong>Review Entries</strong> to check everything
            before final submission.
          </AlertDescription>
        </Alert>

        <Card className="border-[rgba(201,168,76,0.25)]">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 border-b-2">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Innovator Email *</TableHead>
                  <TableHead>Innovation Title *</TableHead>
                  <TableHead>Category *</TableHead>
                  <TableHead>Stage *</TableHead>
                  <TableHead>Description *</TableHead>
                  <TableHead>IP uniqueness *</TableHead>
                  <TableHead>Target Users</TableHead>
                  <TableHead>Keywords</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id} className="align-top">
                    <TableCell className="text-center text-slate-500 font-medium">
                      {index + 1}
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        type="email"
                        placeholder="name@example.com"
                        value={row.submitterEmail}
                        onChange={(e) => updateRow(row.id, 'submitterEmail', e.target.value)}
                        onBlur={() => touch(row.id, 'submitterEmail')}
                        className={inputErrorClass(row.id, 'submitterEmail')}
                      />
                      {getMsg(row.id, 'submitterEmail') && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <XCircle className="w-3 h-3 shrink-0" />
                          {getMsg(row.id, 'submitterEmail')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        placeholder="Title (10–200 chars)"
                        value={row.title}
                        onChange={(e) => updateRow(row.id, 'title', e.target.value)}
                        onBlur={() => touch(row.id, 'title')}
                        className={inputErrorClass(row.id, 'title')}
                      />
                      {getMsg(row.id, 'title') && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <XCircle className="w-3 h-3 shrink-0" />
                          {getMsg(row.id, 'title')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Select
                        value={row.category || undefined}
                        onValueChange={(v) => {
                          updateRow(row.id, 'category', v);
                          touch(row.id, 'category');
                        }}
                      >
                        <SelectTrigger className={inputErrorClass(row.id, 'category')}>
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {getMsg(row.id, 'category') && (
                        <p className="text-xs text-red-500 mt-1">{getMsg(row.id, 'category')}</p>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Select
                        value={row.stage || undefined}
                        onValueChange={(v) => {
                          updateRow(row.id, 'stage', v);
                          touch(row.id, 'stage');
                        }}
                      >
                        <SelectTrigger className={inputErrorClass(row.id, 'stage')}>
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {getMsg(row.id, 'stage') && (
                        <p className="text-xs text-red-500 mt-1">{getMsg(row.id, 'stage')}</p>
                      )}
                    </TableCell>
                    <TableCell className="align-top min-w-[200px]">
                      <Textarea
                        placeholder="How it works, components; for products: process & materials (50–3000)"
                        value={row.description}
                        onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                        onBlur={() => touch(row.id, 'description')}
                        rows={3}
                        maxLength={MAX_DESCRIPTION}
                        className={`resize-none ${inputErrorClass(row.id, 'description')}`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {getMsg(row.id, 'description') ? (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <XCircle className="w-3 h-3 shrink-0" />
                            {getMsg(row.id, 'description')}
                          </p>
                        ) : (
                          <span />
                        )}
                        <span
                          className={`text-xs ${
                            row.description.length > MAX_DESCRIPTION - 200
                              ? 'text-amber-600 font-medium'
                              : 'text-slate-400'
                          }`}
                        >
                          {row.description.length}/{MAX_DESCRIPTION}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top min-w-[180px]">
                      <Textarea
                        placeholder="What makes the IP novel or distinct (20–2000)"
                        value={row.ipUniqueness}
                        onChange={(e) => updateRow(row.id, 'ipUniqueness', e.target.value)}
                        onBlur={() => touch(row.id, 'ipUniqueness')}
                        rows={3}
                        maxLength={MAX_IP_UNIQUENESS}
                        className={`resize-none ${inputErrorClass(row.id, 'ipUniqueness')}`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {getMsg(row.id, 'ipUniqueness') ? (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <XCircle className="w-3 h-3 shrink-0" />
                            {getMsg(row.id, 'ipUniqueness')}
                          </p>
                        ) : (
                          <span />
                        )}
                        <span
                          className={`text-xs ${
                            row.ipUniqueness.length > MAX_IP_UNIQUENESS - 200
                              ? 'text-amber-600 font-medium'
                              : 'text-slate-400'
                          }`}
                        >
                          {row.ipUniqueness.length}/{MAX_IP_UNIQUENESS}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <Textarea
                        placeholder="Optional"
                        value={row.targetUsers}
                        onChange={(e) => updateRow(row.id, 'targetUsers', e.target.value)}
                        rows={2}
                        className="resize-none min-w-[140px]"
                      />
                      {getMsg(row.id, 'targetUsers') && (
                        <p className="text-xs text-red-500 mt-1">{getMsg(row.id, 'targetUsers')}</p>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        placeholder="Comma-separated"
                        value={row.keywords}
                        onChange={(e) => updateRow(row.id, 'keywords', e.target.value)}
                        className="min-w-[120px]"
                      />
                    </TableCell>
                    <TableCell className="align-top min-w-[160px]">
                      <input
                        type="file"
                        accept={FILE_ACCEPT}
                        multiple
                        className="hidden"
                        ref={(el) => {
                          fileInputRefs.current[row.id] = el;
                        }}
                        disabled={row.documents.length >= MAX_DOCS_PER_ROW}
                        onChange={(e) => {
                          appendDocuments(row.id, e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <div className="space-y-1.5">
                        {row.documents.map((f, di) => (
                          <div
                            key={`${f.name}-${di}`}
                            className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 min-w-0"
                          >
                            <FileText className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="text-xs text-emerald-800 truncate flex-1 min-w-0" title={f.name}>
                              {f.name}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-emerald-600 hover:text-red-600 shrink-0"
                              onClick={() => removeDocumentAt(row.id, di)}
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        <div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs border-dashed"
                            disabled={row.documents.length >= MAX_DOCS_PER_ROW}
                            onClick={() => fileInputRefs.current[row.id]?.click()}
                          >
                            <Paperclip className="w-3 h-3 mr-1" />
                            {row.documents.length ? 'Add more' : 'Attach files'}
                          </Button>
                          <p className="text-xs text-slate-500 mt-1">
                            Optional · up to {MAX_DOCS_PER_ROW} files
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="align-top text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={rows.length === 1}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                        onClick={() => removeRow(row.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60 flex items-center justify-between flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={rows.length >= remainingSlots}
              className="text-[var(--navy)]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Row
              {rows.length >= remainingSlots && (
                <span className="text-slate-500 font-normal ml-1">— slot limit reached</span>
              )}
            </Button>
            <span className="text-sm text-slate-500">
              {rows.length} of {remainingSlots} slots · {slotsLeft} remaining
            </span>
          </div>
        </Card>

        {rowsWithoutDoc.length > 0 && (
          <Alert className="mt-4 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {rowsWithoutDoc.length} {rowsWithoutDoc.length === 1 ? 'entry has' : 'entries have'} no
              document attached. Documents are optional but recommended.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-end gap-3 mt-6 flex-wrap">
          {hasAnyErrors && Object.keys(touched).length > 0 && (
            <span className="text-sm text-red-500 flex items-center gap-1.5">
              <XCircle className="w-4 h-4" />
              Fix highlighted errors first
            </span>
          )}
          <Button
            onClick={handleReview}
            disabled={hasAnyErrors}
            className="bg-[var(--gold)] text-[var(--navy)] hover:opacity-90"
          >
            Review Entries
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ——— Phase 2: Review ———
  if (phase === 'review') {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Button
          variant="ghost"
          className="mb-4 text-[var(--navy)]"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to dashboard
        </Button>

        <Card className="mb-6 border-[rgba(201,168,76,0.25)]">
          <CardHeader>
            <CardTitle className="text-[var(--navy)]">Review Your Entries</CardTitle>
            <CardDescription>
              Confirm everything looks correct before final submission. This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-[var(--navy)]">{rows.length}</div>
                <div className="text-xs text-slate-500">innovations</div>
              </div>
              <div className="h-10 w-px bg-slate-200" />
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">
                  {rows.length - rowsWithoutDoc.length}
                </div>
                <div className="text-xs text-slate-500">with documents</div>
              </div>
              {rowsWithoutDoc.length > 0 && (
                <>
                  <div className="h-10 w-px bg-slate-200" />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-600">{rowsWithoutDoc.length}</div>
                    <div className="text-xs text-slate-500">without docs</div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {rowsWithoutDoc.length > 0 && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>
                {rowsWithoutDoc.length} {rowsWithoutDoc.length === 1 ? 'entry has' : 'entries have'}{' '}
                no supporting document.
              </strong>{' '}
              Affected rows: {rowsWithoutDoc.map((r, i) => `#${rows.indexOf(r) + 1}`).join(', ')}.
              You can still submit without them.
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-[rgba(201,168,76,0.25)] mb-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>IP uniqueness</TableHead>
                  <TableHead>Documents</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={row.id} className={row.documents.length === 0 ? 'bg-amber-50/30' : ''}>
                    <TableCell className="font-medium text-slate-500">{i + 1}</TableCell>
                    <TableCell className="text-slate-700">{row.submitterEmail}</TableCell>
                    <TableCell className="max-w-[200px]">
                      <span className="truncate block font-medium text-slate-900">{row.title}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{row.category || '—'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{row.stage}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <p className="text-slate-600 text-sm line-clamp-2">{row.description}</p>
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <p className="text-slate-600 text-sm line-clamp-2">{row.ipUniqueness}</p>
                    </TableCell>
                    <TableCell>
                      {row.documents.length > 0 ? (
                        <ul className="text-emerald-700 text-xs space-y-0.5">
                          {row.documents.map((f, fi) => (
                            <li key={`${f.name}-${fi}`} className="flex items-center gap-1">
                              <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate" title={f.name}>
                                {f.name}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                          No documents
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <Button variant="outline" onClick={() => setPhase('entry')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back & Edit
          </Button>
          <Button
            onClick={handleSubmitAll}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Finalise & Submit All ({rows.length})
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ——— Phase 3: Submitting ———
  if (phase === 'submitting') {
    return (
      <div className="max-w-2xl mx-auto p-6 flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-16 h-16 rounded-full bg-[var(--gold-pale)] border border-[rgba(201,168,76,0.3)] flex items-center justify-center mb-6">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Submitting Innovations…</h2>
        <p className="text-slate-600 text-sm text-center mb-6">
          Please keep this page open. Each innovation is being submitted individually.
        </p>
        <div className="w-full max-w-md space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-700">Progress</span>
            <span className="font-semibold text-[var(--navy)]">
              {progress.done} of {progress.total} submitted
            </span>
          </div>
          <Progress value={progressPct} className="h-3" />
          <p className="text-xs text-slate-500 text-center">
            Submitting {Math.min(progress.done + 1, progress.total)} of {progress.total}…
          </p>
        </div>
      </div>
    );
  }

  // ——— Phase 4: Results ———
  return (
    <div className="max-w-6xl mx-auto p-6">
      <div
        className={`rounded-xl border p-5 mb-6 flex items-center gap-4 ${
          failCount === 0
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-amber-50 border-amber-200'
        }`}
      >
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
            failCount === 0 ? 'bg-emerald-100' : 'bg-amber-100'
          }`}
        >
          {failCount === 0 ? (
            <CheckCircle className="w-6 h-6 text-emerald-600" />
          ) : (
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          )}
        </div>
        <div>
          <h2
            className={`font-semibold text-lg ${
              failCount === 0 ? 'text-emerald-900' : 'text-amber-900'
            }`}
          >
            {failCount === 0
              ? 'All innovations submitted successfully!'
              : 'Batch completed with some issues'}
          </h2>
          <p className={`text-sm mt-0.5 ${failCount === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
            <strong>{successCount}</strong> submitted successfully
            {failCount > 0 && (
              <> · <strong>{failCount}</strong> failed — retry failed entries in a new batch</>
            )}
          </p>
        </div>
      </div>

      <Card className="border-[rgba(201,168,76,0.25)] mb-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-12">#</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submission ID / Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {results.map((r, i) => (
                <TableRow key={r.id} className={!r.success ? 'bg-red-50/50' : ''}>
                  <TableCell className="font-medium text-slate-500">{i + 1}</TableCell>
                  <TableCell className="max-w-[180px]">
                    <span className="truncate block font-medium text-slate-900">{r.title}</span>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{r.submitterEmail}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.stage}</Badge>
                  </TableCell>
                  <TableCell className="text-sm max-w-[200px]">
                    {r.documents.length > 0 ? (
                      <ul className="text-emerald-600 text-xs space-y-0.5">
                        {r.documents.map((f, fi) => (
                          <li key={`${f.name}-${fi}`} className="truncate" title={f.name}>
                            {f.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-slate-400 italic">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.success ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                        Submitted
                      </Badge>
                    ) : (
                      <Badge variant="destructive">Failed</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm max-w-[220px]">
                    {r.success ? (
                      <code className="text-xs bg-slate-100 text-[var(--navy)] px-2 py-0.5 rounded break-all">
                        {r.submissionId}
                      </code>
                    ) : (
                      <span className="text-red-600 text-xs">{r.error}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <Button
          variant="outline"
          onClick={handleReset}
          className="text-[var(--navy)] border-[var(--gold)]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Upload Another Batch
        </Button>
        <Button variant="outline" onClick={onBack}>
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
}
